import { narrators } from '../../..';
import { getPOIFrom } from '../../utils';
import eventBus from '../../utils/emitter-event-bus';
import { renderInline } from '../../utils/inline-markdown';
import { MerchantEventBus } from '../merchant/merchant.model';
import { NavigatorEventBus, POI_ID } from '../navigator/navigator.model';
import { WorldEventBus } from '../world/world.model';
import { Dialog, DialogId, DialogOption, DialogType, NarratorEventBus, NarratorNPC } from './narrator.model';

// Cadence of the trailing-dot loading animation used for delayed transitions.
const LOADING_FRAME_MS = 400;

export class Narrator {
  private narrator!: NarratorNPC;
  private currentOptions: DialogOption[] = [];
  private castTimeout?: ReturnType<typeof setTimeout>;
  private loadingInterval?: ReturnType<typeof setInterval>;
  private readonly titleElement = <HTMLElement>document.querySelector('.js-narrator-title');
  private readonly dialogElement = <HTMLElement>document.querySelector('.js-narrator-dialog');
  private readonly optionsElement = <HTMLElement>document.querySelector('.js-narrator-options');

  constructor() {
    this.handleSelectedOptions();
    eventBus.on(WorldEventBus.initialise, (poi) => this.updateNarrator(poi));
    eventBus.on(NavigatorEventBus.navigationStart, (poi) => this.updateNarrator(poi));
  }

  private handleSelectedOptions(): void {
    this.optionsElement.addEventListener('click', (event) => {
      const optionElement = <HTMLElement>event.target;
      const indexAttr = optionElement.dataset.optionIndex;
      if (indexAttr === undefined) return;
      const option = this.currentOptions[Number(indexAttr)];
      if (!option) return;
      if (option.delayMs && option.delayMs > 0) {
        this.startDelayedTransition(option);
        return;
      }
      this.navigateTo(option);
    });
  }

  private navigateTo(option: DialogOption): void {
    const nextDialogId = typeof option.nextDialogId === 'function' ? option.nextDialogId() : option.nextDialogId;
    this.renderNarrator(nextDialogId);
  }

  // Show a loading animation for `option.delayMs` before resolving and rendering
  // the next node. The `nextDialogId` function is evaluated AFTER the wait so it
  // reflects inventory at reveal time (e.g. fishing's full-bag release routing).
  private startDelayedTransition(option: DialogOption): void {
    this.clearPendingTransition();
    eventBus.emit(NarratorEventBus.dialogStart);
    this.currentOptions = [];
    this.resetOptions();
    this.startLoadingAnimation(option.loadingText ?? 'Loading');
    this.castTimeout = setTimeout(() => {
      this.castTimeout = undefined;
      this.stopLoadingAnimation();
      this.navigateTo(option);
    }, option.delayMs);
  }

  private startLoadingAnimation(base: string): void {
    let frame = 0;
    this.dialogElement.innerText = base;
    this.loadingInterval = setInterval(() => {
      frame = (frame + 1) % 4;
      this.dialogElement.innerText = base + '.'.repeat(frame);
    }, LOADING_FRAME_MS);
  }

  private stopLoadingAnimation(): void {
    if (this.loadingInterval !== undefined) {
      clearInterval(this.loadingInterval);
      this.loadingInterval = undefined;
    }
  }

  // Cancel any in-flight delayed transition (loader + timeout) without navigating.
  private clearPendingTransition(): void {
    this.stopLoadingAnimation();
    if (this.castTimeout !== undefined) {
      clearTimeout(this.castTimeout);
      this.castTimeout = undefined;
    }
  }

  private updateNarrator(poi: POI_ID): void {
    // Navigating away mid-cast must cancel the pending reveal so its effects
    // never apply against a different narrator after the player leaves.
    this.clearPendingTransition();
    const narrator = this.fetchNarrator(poi);

    if (!narrator) {
      this.resetNarrator();
      return;
    }

    this.narrator = narrator;
    this.renderNarrator();
  }

  private fetchNarrator(poiId: POI_ID): NarratorNPC | undefined {
    const poi = getPOIFrom(poiId);
    if (!poi?.narratorId) return undefined;
    return narrators.find((n) => n.id === poi.narratorId);
  }

  private renderNarrator(dialogId: DialogId = 'start'): void {
    dialogId === 'start' ? eventBus.emit(NarratorEventBus.narratorStart) : eventBus.emit(NarratorEventBus.dialogStart);

    const dialog = this.getDialog(dialogId);
    this.renderTitle(dialog);
    if (!dialog) {
      this.resetDialog();
      this.resetOptions();
      return;
    }

    if (dialog.effects && dialog.effects.length > 0) {
      eventBus.emit(NarratorEventBus.nodeEntered, { effects: dialog.effects });
    }

    if (dialog.openMerchant) {
      eventBus.emit(MerchantEventBus.open);
    }

    this.renderDialog(dialog);
    this.renderOptions(dialog);
  }

  private getDialog(dialogId: DialogId): Dialog | undefined {
    return this.narrator.dialog.find((option) => option.id === dialogId);
  }

  private renderTitle(dialog: Dialog | undefined): void {
    if (dialog?.type === DialogType.description) {
      this.resetTitle();
      return;
    }

    this.titleElement.innerText = dialog?.name || this.narrator.name;
  }

  private renderDialog(dialog: Dialog): void {
    this.dialogElement.innerHTML = renderInline(dialog.text);
  }

  private renderOptions(dialog: Dialog): void {
    this.resetOptions();
    this.currentOptions = [];
    if (!dialog.options) return;

    for (const option of dialog.options) {
      if (option.condition && !option.condition()) continue;
      const index = this.currentOptions.length;
      this.currentOptions.push(option);
      const listElement = document.createElement('button');
      listElement.innerText = option.text;
      listElement.setAttribute('data-option-index', String(index));
      listElement.classList.add('option');
      const linkElement = document.createElement('li');
      linkElement.appendChild(listElement);
      const optionsElement = <HTMLElement>document.querySelector('.js-narrator-options');
      optionsElement.appendChild(linkElement);
    }
  }

  private resetNarrator(): void {
    this.resetTitle();
    this.resetDialog();
    this.resetOptions();
  }

  private resetTitle(): void {
    this.titleElement.innerText = '';
  }

  private resetDialog(): void {
    this.dialogElement.innerText = '';
  }

  private resetOptions(): void {
    this.optionsElement.innerHTML = '';
  }
}
